import { WorkerRequirementDomain } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type WorkerRequirement } from "@src/appV2/redesign/Documents/api/types";
import { filterPostBookingRequirementsForShift } from "@src/appV2/redesign/Documents/utils/filterPostBookingRequirementsForShift";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useMemo } from "react";

import { useGetRequirementsForWorkplaces } from "./useGetRequirementsForWorkplaces";

export interface ShiftPendingPostBookingRequirementsInput {
  shiftId: string;
  workplaceId?: string;
  workplaceType?: string;
  msa?: string;
  state?: string;
  region?: string;
  qualification: string;
}

export function toShiftPendingPostBookingRequirementsInput(
  shift: Pick<Shift, "_id" | "agentReq" | "facility">
): ShiftPendingPostBookingRequirementsInput {
  return {
    shiftId: shift._id,
    workplaceId: shift.facility.userId,
    workplaceType: shift.facility.type,
    msa: shift.facility.fullAddress?.metropolitanStatisticalArea,
    state: shift.facility.state ?? shift.facility.fullAddress?.state,
    region: shift.facility.fullAddress?.region,
    qualification: shift.agentReq,
  };
}

interface ShiftPendingPostBookingRequirementsResult {
  requirements: WorkerRequirement[];
  count: number;
}

interface UsePendingPostBookingRequirementsForShiftsResult {
  resultsByShiftId: Record<string, ShiftPendingPostBookingRequirementsResult>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

const EMPTY_RESULTS: Record<string, ShiftPendingPostBookingRequirementsResult> = {};

export function usePendingPostBookingRequirementsForShifts(
  shifts: ShiftPendingPostBookingRequirementsInput[]
): UsePendingPostBookingRequirementsForShiftsResult {
  const { workplaceIds, qualifications } = useMemo(() => {
    const workplaceIdSet = new Set<string>();
    const qualificationSet = new Set<string>();
    for (const shift of shifts) {
      if (isDefined(shift.workplaceId)) {
        workplaceIdSet.add(shift.workplaceId);
      }
      qualificationSet.add(shift.qualification);
    }

    return {
      workplaceIds: Array.from(workplaceIdSet).toSorted(),
      qualifications: Array.from(qualificationSet).toSorted(),
    };
  }, [shifts]);

  const {
    data: workplaceRequirements,
    isLoading,
    isSuccess,
    isError,
  } = useGetRequirementsForWorkplaces(
    {
      workplaceIds,
      qualifications,
      domain: WorkerRequirementDomain.FLEX,
      visibleToWorker: true,
    },
    { enabled: workplaceIds.length > 0 && qualifications.length > 0 }
  );

  return useMemo(() => {
    if (shifts.length === 0 || workplaceIds.length === 0) {
      return { resultsByShiftId: EMPTY_RESULTS, isLoading: false, isSuccess: true, isError: false };
    }

    if (!isSuccess || !isDefined(workplaceRequirements)) {
      return { resultsByShiftId: EMPTY_RESULTS, isLoading, isSuccess, isError };
    }

    const resultsByShiftId: Record<string, ShiftPendingPostBookingRequirementsResult> = {};
    for (const shift of shifts) {
      if (!isDefined(shift.workplaceId)) {
        resultsByShiftId[shift.shiftId] = { requirements: [], count: 0 };
        continue;
      }

      const requirements =
        filterPostBookingRequirementsForShift({
          workplace: {
            id: shift.workplaceId,
            workplaceType: shift.workplaceType,
            msa: shift.msa,
            state: shift.state,
            region: shift.region,
          },
          qualifications: [shift.qualification],
          workplaceRequirements,
        }) ?? [];

      resultsByShiftId[shift.shiftId] = { requirements, count: requirements.length };
    }

    return { resultsByShiftId, isLoading, isSuccess, isError };
  }, [shifts, workplaceIds, workplaceRequirements, isLoading, isSuccess, isError]);
}
